import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import rp from './ReportsPage.module.css'
import styles from './reports/Schedules.module.css'
import { dmy } from './wages/format'
import { cellText, isNumeric, noteText, scopeLabel, tableLayout, type ReportResult } from './reports/model'
import { whenText } from './reports/schedules'

// The page a scheduled report's link opens (handoff §7l.6): no CONTROL
// login, read-only, this one report as it was built for that run. The token
// in the address is the whole credential, so nothing here signs in, sends a
// workspace, or links anywhere else in CONTROL; rows do not drill.

interface Shared {
  report: Omit<ReportResult, 'options'> & { rows: { cells: ReportResult['rows'][number]['cells'] }[] }
  sent_at: string
  period: { from: string | null; to: string | null }
  cadence: string
  from_name: string
  expires_at: string
}

const GONE = 'This link has expired or been switched off. Ask whoever sent it for a new one.'

export function SharedReportPage() {
  const { token = '' } = useParams()
  const [data, setData] = useState<Shared | null>(null)
  const [error, setError] = useState<string | null>(null)

  // The token is in the address: no request from this page may carry it on.
  useEffect(() => {
    const m = document.createElement('meta')
    m.name = 'referrer'
    m.content = 'no-referrer'
    document.head.appendChild(m)
    return () => m.remove()
  }, [])

  useEffect(() => {
    let live = true
    // Plain fetch: no Firebase token and no X-Tenant-Id, ever.
    fetch(`/api/shared/report/${encodeURIComponent(token)}`, { credentials: 'omit', referrerPolicy: 'no-referrer' })
      .then(async (r) => {
        const body = await r.json().catch(() => null)
        if (!live) return
        if (r.ok) setData(body)
        else setError(r.status === 404 ? body?.error || GONE : 'The report could not be loaded. Try again in a minute.')
      })
      .catch(() => live && setError('The server could not be reached. Check the connection and try again.'))
    return () => {
      live = false
    }
  }, [token])

  const rep = data?.report
  const cols = rep?.cols || []
  const layout = tableLayout(cols)
  const chartMax = Math.max(1, ...(rep?.chart || []).map((b) => b.v || 0))

  return (
    <div className={styles.sharedPage}>
      <div className={styles.sharedInner}>
        <div className={styles.sharedBrand}>Dataphyll CONTROL · shared report</div>
        {!data && !error && <p className={rp.muted}>Loading the report…</p>}
        {error && (
          <div className={styles.sharedGone} role="alert">
            {error}
          </div>
        )}
        {data && rep && (
          <>
            <div className={styles.sharedHead}>
              <h1 className={rp.shellTitle}>{rep.name}</h1>
              <span className={rp.scope} data-testid="shared-scope">
                {scopeLabel(rep.scope)}
              </span>
            </div>
            <p className={styles.sharedMeta}>
              Sent by {data.from_name} on {whenText(data.sent_at)}. These are the figures as they stood then. The link works until {dmy(data.expires_at.slice(0, 10))}.
            </p>

            {rep.kpis.length > 0 && (
              <div className={rp.kpis}>
                {rep.kpis.map((k) => (
                  <div key={k.label} className={rp.kpi}>
                    <div className={rp.kpiLabel}>{k.label}</div>
                    <div className={rp.kpiValue}>{cellText(k.value, k.fmt)}</div>
                    {k.note && <div className={rp.kpiNote}>{noteText(k.note)}</div>}
                  </div>
                ))}
              </div>
            )}

            {rep.chart && rep.chart.length > 0 && (
              <div className={rp.chartCard}>
                <div className={rp.eyebrow}>{rep.chart_unit}</div>
                <div className={rp.chart} role="img" aria-label={rep.chart_unit}>
                  {rep.chart.map((b, i) => (
                    <div key={b.label + i} className={rp.bar}>
                      <span className={rp.barValue}>{b.v ? String(b.v) : ''}</span>
                      <span className={rp.barFill} style={{ height: Math.max(2, Math.round(((b.v || 0) / chartMax) * 100)) + '%', background: b.v ? 'var(--dp-accent)' : 'var(--dp-line)' }} title={b.label} />
                      <span className={rp.barLabel}>{b.date ? dmy(b.date) : b.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {cols.length > 0 && (
              <div className={rp.table} role="table" aria-label={rep.name}>
                <div className={rp.headRow} role="row" style={layout}>
                  {cols.map((c, i) => (
                    <span key={i} role="columnheader" className={isNumeric(c.fmt) ? rp.num : undefined}>
                      {c.label}
                    </span>
                  ))}
                </div>
                {rep.rows.length === 0 ? (
                  <div className={rp.empty}>Nothing in this period.</div>
                ) : (
                  rep.rows.map((r, i) => (
                    <div key={i} role="row" className={rp.row} style={layout}>
                      {r.cells.map((v, j) => (
                        <span key={j} role="cell" className={`${j === 0 ? rp.firstCell : ''} ${cols[j] && isNumeric(cols[j].fmt) ? rp.num : ''}`}>
                          {cellText(v, cols[j]?.fmt || 'text')}
                        </span>
                      ))}
                    </div>
                  ))
                )}
              </div>
            )}

            {rep.notes.length > 0 && (
              <ul className={rp.notes}>
                {rep.notes.map((n) => (
                  <li key={n}>{n}</li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </div>
  )
}
